import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Translate as t } from '../../config/Translate';

// FEAT-052 (#493): 로그인 성공 직후 동의가 필요한 이용자에게 띄우는 모달.
//
// 기존 흐름은 `/IubendaLogin` 전용 화면으로 전체 페이지를 리다이렉트하고, 동의 후 다시
// 리다이렉트했다 — 리로드가 두 번 일어나 이용자는 "또 로그인했다"로 체감한다. 본 컴포넌트는
// 라우트 이동 없이 같은 화면 위에 떠서 동의를 받고, 끝나면 그대로 진입시킨다.
//
// 문구 원문은 prop 으로 받는다. 어디서 오는지(정적 파일 / 서버 / RAYConsent)를 이 컴포넌트가
// 알지 않는다 — 개인정보 처리방침 최종본이 10/12 에 도착할 예정이라, 문구를 코드에 박아두면
// 그날 재배포가 필요해지기 때문이다.

// 스크롤 끝 판정 여유분(px). 브라우저·줌 배율에 따라 scrollTop 이 소수점으로 끝나
// 정확히 같아지지 않는 경우가 있어 여유를 둔다.
const SCROLL_EPSILON = 4;

const FOCUSABLE = 'a[href], button, [tabindex]:not([tabindex="-1"])';

const ConsentModal = ({
    title,
    bodyHtml,
    bodyText,
    version,
    submitting = false,
    // 문구를 못 받아온 상태에서는 동의 버튼이 열리면 안 된다. 판 번호 없는 동의는
    // 「무엇에 동의했는지」를 가리키지 못해 기록으로서 무의미하고, 그런 기록은 아예 남기지
    // 않는 것이 맞다(틀린 증거를 쌓느니 안 쌓는다). 본문이 짧아 스크롤이 없는 경우
    // reachedEnd 가 바로 true 가 되므로, 스크롤 판정만으로는 이 상황을 못 막는다.
    canSubmit = true,
    // 조회 실패 시 유일한 복구 수단. 없으면 이용자에게 남는 선택지가 로그아웃뿐이다.
    onRetry,
    errorMessage = '',
    onAgree,
    onDecline,
}) => {
    // 끝까지 읽어야 동의 버튼이 열린다. 다만 **스크롤이 필요 없는 경우에도 열어야 한다** —
    // 문구가 짧거나 화면이 크면 스크롤이 생기지 않아 버튼이 영영 비활성으로 남는다.
    const [reachedEnd, setReachedEnd] = useState(false);
    const scrollRef = useRef(null);
    const dialogRef = useRef(null);

    const evaluateScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const noScrollNeeded = el.scrollHeight <= el.clientHeight + SCROLL_EPSILON;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_EPSILON;
        if (noScrollNeeded || atBottom) setReachedEnd(true);
    }, []);

    // 본문이 바뀌거나(비동기 로딩) 창 크기가 바뀌면 다시 판정한다.
    useEffect(() => {
        evaluateScroll();
        window.addEventListener('resize', evaluateScroll);
        return () => window.removeEventListener('resize', evaluateScroll);
    }, [evaluateScroll, bodyHtml, bodyText]);

    // 열려 있는 동안 뒤 화면 스크롤을 막는다. 차단 환경에서도 터지지 않게 방어.
    useEffect(() => {
        let prev = '';
        try {
            prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        } catch (e) { /* no-op */ }
        return () => {
            try { document.body.style.overflow = prev; } catch (e) { /* no-op */ }
        };
    }, []);

    // 포커스를 모달 안으로 옮긴다(스크린리더·키보드 사용자가 뒤 화면을 돌아다니지 않도록).
    useEffect(() => {
        try { dialogRef.current?.focus(); } catch (e) { /* no-op */ }
    }, []);

    // Esc 로 닫지 않는다 — 동의 또는 로그아웃 둘 중 하나를 선택해야 하는 화면이다.
    // Tab 은 모달 안에서만 돈다. 뒤에는 로그인 셸(메뉴 등)이 마운트돼 있어 포커스가 새면
    // 키보드 사용자가 모달 밖을 돌아다닌다.
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            return;
        }
        if (e.key !== 'Tab') return;
        const root = dialogRef.current;
        if (!root) return;
        const items = Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === root)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    // 본문 링크는 **새 탭으로만** 연다. 같은 탭으로 이동하면 모달과 진행 상태가 사라진다.
    // http(s)·mailto 외의 스킴은 열지 않는다 — 본문은 우리가 등록한 원문이지만, 같은 origin 에
    // 토큰이 있으므로 신뢰 사슬이 한 번 뚫려도 여기서 한 겹 더 막는다.
    const handleBodyClick = (e) => {
        const a = e.target?.closest?.('a[href]');
        if (!a) return;
        e.preventDefault();
        const href = a.getAttribute('href') || '';
        if (!/^(https?:|mailto:)/i.test(href.trim())) {
            console.warn('[ConsentModal] blocked policy link', {
                handler: 'ConsentModal.handleBodyClick',
                scheme: href.trim().split(':')[0].slice(0, 16),
                reason: 'only http(s) and mailto links are opened from the policy body',
            });
            return;
        }
        try { window.open(href, '_blank', 'noopener,noreferrer'); } catch (err) { /* no-op */ }
    };

    const canAgree = reachedEnd && !submitting && canSubmit;

    return (
        <div
            className="consent-modal-backdrop"
            style={{
                position: 'fixed', inset: 0, zIndex: 20000,
                background: 'rgba(17,17,17,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px',
            }}
            onKeyDown={handleKeyDown}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="consent-modal-title"
                tabIndex={-1}
                style={{
                    background: '#fff', borderRadius: '10px',
                    width: '100%', maxWidth: '680px',
                    maxHeight: 'calc(100vh - 32px)',
                    display: 'flex', flexDirection: 'column',
                    outline: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
                }}
            >
                <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #e9e9e9' }}>
                    <h5 id="consent-modal-title" style={{ margin: 0, fontWeight: 700 }}>
                        {title || t('consent.title')}
                    </h5>
                    <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#666' }}>
                        {t('consent.readToEnd')}
                    </p>
                </div>

                <div
                    ref={scrollRef}
                    onScroll={evaluateScroll}
                    onClick={handleBodyClick}
                    tabIndex={0}
                    aria-label={title || t('consent.title')}
                    style={{
                        overflowY: 'auto', padding: '16px 20px',
                        flex: '1 1 auto', minHeight: '160px',
                        fontSize: '13.5px', lineHeight: 1.7, color: '#333',
                    }}
                >
                    {bodyHtml
                        // 문구는 우리가 등록·관리하는 처리방침 원문이다(이용자 입력이 아니다).
                        ? <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                        : <div style={{ whiteSpace: 'pre-wrap' }}>{bodyText || ''}</div>}
                </div>

                <div style={{ padding: '12px 20px 18px', borderTop: '1px solid #e9e9e9' }}>
                    {!reachedEnd && canSubmit && (
                        <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: '#b36b00' }}>
                            {t('consent.scrollHint')}
                        </p>
                    )}
                    {errorMessage && (
                        <p className="text-danger" style={{ margin: '0 0 8px', fontSize: '13px' }}>
                            {errorMessage}
                        </p>
                    )}
                    <button
                        type="button"
                        className="btn btn-primary btn-block"
                        disabled={!canAgree}
                        onClick={onAgree}
                    >
                        {submitting ? t('consent.submitting') : t('consent.agree')}
                    </button>
                    {onRetry && (
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-block mt-2"
                            disabled={submitting}
                            onClick={onRetry}
                        >
                            {t('consent.retry')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-block mt-2"
                        disabled={submitting}
                        onClick={onDecline}
                    >
                        {t('consent.declineAndLogout')}
                    </button>
                    {version && (
                        <p style={{ margin: '10px 0 0', fontSize: '11.5px', color: '#999', textAlign: 'right' }}>
                            {t('consent.versionLabel')} {version}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsentModal;
